import {
  DEFAULT_CLOZE_TIME_LIMIT_SECONDS,
  DIGEST_EXAMPLE_SENTENCE_TAG_ID,
  collectClozeItemsFromVocList,
  resolveClozeSlot,
  chunkArrayForCloze,
  getPrimaryBaseWord,
  formatClozeDuration,
  formatClozeDateTime,
  COMPLETION_TYPE,
  isCompletionType,
  collectWrongItemsFromGroups,
  nirvanaSetName,
  buildVocClozeAssignmentPayload as buildSharedClozeAssignmentPayload
} from '../cloze/wordClozeUtils'

export { COMPLETION_TYPE, isCompletionType, collectWrongItemsFromGroups, nirvanaSetName }
export const VOC_WORD_COMPLETION_STORAGE_KEY = 'vocWordCompletionPayload'
export const DEFAULT_COMPLETION_TIME_LIMIT_SECONDS = DEFAULT_CLOZE_TIME_LIMIT_SECONDS
export { DIGEST_EXAMPLE_SENTENCE_TAG_ID, collectClozeItemsFromVocList, formatClozeDuration, formatClozeDateTime, getPrimaryBaseWord }

export function givenLetterCount (correctForm) {
  const length = String(correctForm || '').length
  if (!length) return 0
  const count = length > 5 ? 3 : 2
  return Math.min(length, count)
}

export function splitCorrectFormForLetters (correctForm) {
  const text = String(correctForm || '')
  if (!text) {
    return { firstLetter: '', restChars: [] }
  }
  const givenCount = givenLetterCount(text)
  return {
    firstLetter: text.slice(0, givenCount),
    restChars: text.slice(givenCount).split('')
  }
}

export function hydrateCompletionSlot (slot, groupIndex = 0) {
  const correctForm = String(slot.correctForm || slot.baseWord || '')
  const { firstLetter, restChars } = splitCorrectFormForLetters(correctForm)
  const slotIndex = slot.slotIndex ?? 0
  return {
    slotIndex,
    itemKey:
      slot.itemKey ||
      `${groupIndex}-${slotIndex}-${slot.vocabularyId || slot.baseWord}`,
    vocabularyId: slot.vocabularyId ?? null,
    baseWord: slot.baseWord || '',
    chineseText: slot.chineseText || '',
    sentenceFull: slot.sentenceFull || '',
    sentenceBefore: slot.sentenceBefore || '',
    sentenceAfter: slot.sentenceAfter || '',
    correctForm,
    matchedInSentence: !!slot.matchedInSentence,
    firstLetter,
    restChars,
    letterInputs: Array.from({ length: restChars.length }, () => ''),
    studentAnswer: '',
    isCorrect: false
  }
}

export function buildCompletionGroups (items, groupSize = 5) {
  const validItems = (items || []).filter((item) => {
    const baseWord = (item.englishText || '').trim()
    const sentence = (item.exampleSentence || '').trim()
    return baseWord && sentence
  })

  const rawGroups = chunkArrayForCloze(validItems, groupSize)
  return rawGroups.map((groupItems, groupIndex) => {
    const slots = groupItems.map((item, slotIndex) => {
      const baseWord = item.englishText.trim()
      const sentence = item.exampleSentence.trim()
      const resolved = resolveClozeSlot(sentence, baseWord)
      const uniqueKey = item.vocabularyId != null
        ? `${groupIndex}-${slotIndex}-${item.vocabularyId}`
        : `${groupIndex}-${slotIndex}-${baseWord}-${sentence}`
      return hydrateCompletionSlot(
        {
          slotIndex,
          itemKey: uniqueKey,
          vocabularyId: item.vocabularyId ?? item.vocId ?? null,
          baseWord: resolved.baseWord,
          chineseText: item.chineseText || '',
          sentenceFull: sentence,
          sentenceBefore: resolved.sentenceBefore,
          sentenceAfter: resolved.sentenceAfter,
          correctForm: resolved.correctForm,
          matchedInSentence: resolved.matchedInSentence
        },
        groupIndex
      )
    })

    return {
      groupIndex,
      slots,
      wordBank: []
    }
  })
}

export function normalizeCompletionAssignmentGroups (groups) {
  return (groups || []).map((group, index) => ({
    groupIndex: group.groupIndex ?? index,
    wordBank: [],
    slots: (group.slots || []).map((slot, slotIndex) =>
      hydrateCompletionSlot(
        {
          ...slot,
          slotIndex: slot.slotIndex ?? slotIndex
        },
        index
      )
    )
  }))
}

export function itemsFromCompletionGroups (groups) {
  const items = []
  for (const group of groups || []) {
    for (const slot of group.slots || []) {
      items.push({
        englishText: slot.baseWord,
        chineseText: slot.chineseText || '',
        exampleSentence: slot.sentenceFull || '',
        vocabularyId: slot.vocabularyId ?? null
      })
    }
  }
  return items
}

export function assembleStudentAnswer (slot) {
  return `${slot.firstLetter || ''}${(slot.letterInputs || []).join('')}`
}

export function isCompletionSlotFilled (slot) {
  if (!(slot.restChars || []).length) return true
  return (slot.letterInputs || []).every((ch) => String(ch || '').length > 0)
}

export function gradeCompletionSlot (slot) {
  const answer = assembleStudentAnswer(slot)
  slot.studentAnswer = answer
  slot.isCorrect =
    answer.toLowerCase() === String(slot.correctForm || '').toLowerCase()
  return slot.isCorrect
}

export function buildVocCompletionAssignmentPayload (opts) {
  const payload = buildSharedClozeAssignmentPayload({
    ...opts,
    payload: {
      ...(opts.payload || {}),
      type: COMPLETION_TYPE
    }
  })
  payload.groups = (payload.groups || []).map((group) => ({
    ...group,
    wordBank: (group.slots || []).map((slot, index) => ({
      wordKey: `slot-${slot.slotIndex ?? index}`,
      baseWord: slot.correctForm || slot.baseWord || '',
      placedSlotIndex: null
    }))
  }))
  return payload
}

export function buildVocStudentCompletionTestPayload ({
  payload,
  groups,
  groupResults,
  studentId,
  createdBy,
  isNirvanaSession,
  groupSize,
  timeLimitSeconds,
  totalElapsedSeconds,
  totalCorrect,
  totalQuestions,
  accuracyPercent
}) {
  return {
    studentId: Number(studentId) || 0,
    createdBy: Number(createdBy) || 0,
    subjectName: payload?.subjectName || '',
    setName: payload?.setName || '',
    type: COMPLETION_TYPE,
    status: 'finished',
    isNirvanaSession: !!isNirvanaSession,
    groupSize: Number(groupSize) || 5,
    timeLimitSeconds: Number(timeLimitSeconds) || DEFAULT_COMPLETION_TIME_LIMIT_SECONDS,
    totalElapsedSeconds: Number(totalElapsedSeconds) || 0,
    totalCorrect: Number(totalCorrect) || 0,
    totalQuestions: Number(totalQuestions) || 0,
    accuracyPercent: Number(accuracyPercent) || 0,
    groupResults: (groups || []).map((group, index) => {
      const result =
        (groupResults || []).find((item) => item.groupIndex === group.groupIndex) || {}
      return {
        groupIndex: group.groupIndex ?? index,
        correct: result.correct ?? (group.slots || []).filter((slot) => slot.isCorrect).length,
        total: result.total ?? (group.slots || []).length,
        timedOut: !!result.timedOut,
        elapsedSeconds: Number(result.elapsedSeconds) || 0,
        slots: (group.slots || []).map((slot) => ({
          slotIndex: slot.slotIndex,
          vocabularyId: Number(slot.vocabularyId) || 0,
          baseWord: slot.baseWord || '',
          correctForm: slot.correctForm || '',
          studentAnswer: slot.studentAnswer || assembleStudentAnswer(slot),
          isCorrect: !!slot.isCorrect,
          sentenceFull: slot.sentenceFull || '',
          sentenceBefore: slot.sentenceBefore || '',
          sentenceAfter: slot.sentenceAfter || '',
          matchedInSentence: !!slot.matchedInSentence
        }))
      }
    })
  }
}

export function buildVocCompletionAssignmentResultPayload (opts) {
  return {
    ...buildVocStudentCompletionTestPayload(opts),
    assignmentId: Number(opts.assignmentId) || 0
  }
}

export function countCompletionExerciseItems (items, groupSize = 5) {
  return buildCompletionGroups(items, groupSize).reduce(
    (sum, group) => sum + group.slots.length,
    0
  )
}
